import fetchEndpoint from '../services/fetchEndpoint';
import type { AnalyticsDataRequest } from './buildAnalyticsDataRequest';

type FetchAnalyticsPageParams = {
  request: AnalyticsDataRequest;
  authorization: unknown;
  setIsAuthenticated: ((value: boolean) => void) | undefined;
};

type FetchAnalyticsPageResult = {
  rows: unknown[];
  rawTotal: number;
  filteredTotal: number | null;
};

export async function fetchAnalyticsPage({
  request,
  authorization,
  setIsAuthenticated,
}: FetchAnalyticsPageParams): Promise<FetchAnalyticsPageResult> {
  const response = await (fetchEndpoint as any)(
    request.url,
    authorization,
    setIsAuthenticated,
    request.method === 'POST' ? { method: 'POST', body: request.body } : null,
  );

  const rows = Array.isArray(response?.payload) ? response.payload : [];
  const rawTotal = Number(response?.paging?.totalElements ?? rows.length);
  const filteredRaw =
    response?.paging?.filteredElements ?? response?.paging?.filteredCount ?? response?.filteredCount;
  const filteredTotal =
    filteredRaw != null && Number.isFinite(Number(filteredRaw)) ? Number(filteredRaw) : null;

  return { rows, rawTotal, filteredTotal };
}

type AppendAnalyticsPagesParams = {
  isSummaryMatchPost: boolean;
  pageSize: number;
  firstPageRows: unknown[];
  fetchPage: (pageNum: number) => Promise<FetchAnalyticsPageResult>;
  knownTotalCount?: number | null;
  maxPostPages?: number;
  maxGetPages?: number;
  getTotalElements?: number;
};

export async function appendAnalyticsPages({
  isSummaryMatchPost,
  pageSize,
  firstPageRows,
  fetchPage,
  knownTotalCount = null,
  maxPostPages = 6,
  maxGetPages = 40,
  getTotalElements,
}: AppendAnalyticsPagesParams): Promise<unknown[]> {
  let completeData = [...firstPageRows];

  if (firstPageRows.length < pageSize) {
    return completeData;
  }

  if (isSummaryMatchPost) {
    if (knownTotalCount == null || knownTotalCount <= firstPageRows.length) {
      return completeData;
    }

    const totalPages = Math.min(Math.ceil(knownTotalCount / pageSize), maxPostPages);

    for (let page = 1; page < totalPages; page += 1) {
      const { rows } = await fetchPage(page);
      if (!rows.length) break;
      completeData = [...completeData, ...rows];
      if (rows.length < pageSize) break;
      if (completeData.length >= knownTotalCount) break;
    }

    return completeData;
  }

  if (!Number.isFinite(getTotalElements) || getTotalElements <= firstPageRows.length) {
    return completeData;
  }

  const totalPages = Math.min(Math.ceil(getTotalElements / pageSize), maxGetPages);
  for (let page = 1; page < totalPages; page += 1) {
    const { rows } = await fetchPage(page);
    if (!rows.length) break;
    completeData = [...completeData, ...rows];
    if (rows.length < pageSize) break;
  }

  return completeData;
}
